<div class="work-order-page p-6">
  <div class="mb-6">
    <h1 class="page-title flex items-center gap-3">
      <span class="page-title__icon">
        <mat-icon>description</mat-icon>
      </span>
      {{ editMode ? 'Edit Work Order' : 'Create Work Order' }}
    </h1>
    <p class="page-subtitle">Generate work order for project planning</p>
  </div>

  <div *ngIf="loading" class="loading-state">
    <div class="loading-state__spinner"></div>
    <p>{{ editMode ? 'Loading work order...' : 'Saving work order...' }}</p>
  </div>

  <form [formGroup]="workOrderForm" (ngSubmit)="onSubmit()" *ngIf="!loading || editMode">
    <!-- Project Information -->
    <section class="form-section">
      <h2 class="section-title">Project Information</h2>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6">
          <mat-label>Project **</mat-label>
          <mat-form-field appearance="outline">
            <mat-select formControlName="project_id" (selectionChange)="onProjectChange()">
              <mat-option value="">Select Project</mat-option>
              <mat-option *ngFor="let project of projects" [value]="project.id">
                {{ project.project_name }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="showFieldError('project_id')">Project is required</mat-error>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Work Order Number</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="work_order_number" [readonly]="editMode" placeholder="Auto-generated if left empty">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Issue Date **</mat-label>
          <mat-form-field appearance="outline">
            <input matInput type="date" formControlName="issue_date">
            <mat-error *ngIf="showFieldError('issue_date')">Issue date is required</mat-error>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Purchase Order Number</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="purchase_order_number">
          </mat-form-field>
        </div>
      </div>
    </section>

    <!-- Client Information -->
    <section class="form-section">
      <h2 class="section-title">Client Information</h2>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-12">
          <mat-label>Select Client</mat-label>
          <mat-form-field appearance="outline">
            <mat-select formControlName="client_id" (selectionChange)="onClientChange($event.value)">
              <mat-option value="">From project / manual entry</mat-option>
              <mat-option *ngFor="let client of clients" [value]="client.client_id">
                {{ client.client_name || (client.client_first_name + ' ' + client.client_last_name) }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <p class="field-hint">Pick a client linked to project to autofill details</p>
        </div>

        <div class="col-span-6">
          <mat-label>Client Name **</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_name">
            <mat-error *ngIf="showFieldError('client_name')">Client name is required</mat-error>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Client Address</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="client_address" rows="2"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>CIN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_cin">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>PAN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_pan" maxlength="10">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>TAN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_tan">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>GSTIN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_gstn" maxlength="15">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Name</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_contact_name">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Phone</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="client_contact_phone">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Email</mat-label>
          <mat-form-field appearance="outline">
            <input matInput type="email" formControlName="client_contact_email">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Bill To Address</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="bill_to_address" rows="2"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Ship To Address</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="ship_to_address" rows="2"></textarea>
          </mat-form-field>
          <button type="button" class="copy-address-btn" (click)="copyBillToShip()">
            <mat-icon>content_copy</mat-icon>
            Copy from bill to address
          </button>
        </div>
      </div>
    </section>

    <!-- Vendor Information -->
    <section class="form-section">
      <h2 class="section-title">Vendor Information</h2>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6">
          <mat-label>Select Vendor</mat-label>
          <mat-form-field appearance="outline">
            <mat-select formControlName="vendor_id" (selectionChange)="onVendorSelectionChange($event.value)">
              <mat-option value="manual">Manual entry</mat-option>
              <mat-option *ngFor="let vendor of vendors" [value]="vendor.id || vendor.vendor_id">
                {{ vendor.company_name || vendor.vendor_name || vendor.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Vendor Name **</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_name" [readonly]="isVendorFromList">
            <mat-error *ngIf="showFieldError('vendor_name')">Vendor name is required</mat-error>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Vendor Address</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="vendor_address" rows="2"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Vendor GSTIN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_gstn" maxlength="15">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Vendor PAN</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_pan" maxlength="10">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Name</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_contact_name">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Phone</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_contact_phone">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Contact Email</mat-label>
          <mat-form-field appearance="outline">
            <input matInput type="email" formControlName="vendor_contact_email">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Office Telephone</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="vendor_off_tel">
          </mat-form-field>
        </div>
      </div>
    </section>

    <!-- Shipping Details -->
    <section class="form-section">
      <h2 class="section-title">Shipping Details</h2>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6">
          <mat-label>Ship Via</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="ship_via">
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>CIF</mat-label>
          <mat-form-field appearance="outline">
            <input matInput formControlName="cif">
          </mat-form-field>
        </div>
      </div>
    </section>

    <!-- Items -->
    <section class="form-section">
      <div class="section-header">
        <h2 class="section-title">Items</h2>
        <button type="button" mat-raised-button color="primary" (click)="addItem()">
          <mat-icon>add</mat-icon>
          Add Item
        </button>
      </div>

      <div formArrayName="items">
        <div *ngFor="let item of items.controls; let i = index" [formGroupName]="i" class="item-card">
          <div class="item-card__header">
            <h3 class="item-card__title">Item {{ i + 1 }}</h3>
            <button type="button" mat-icon-button color="warn" (click)="removeItem(i)" *ngIf="items.length > 1">
              <mat-icon>delete</mat-icon>
            </button>
          </div>

          <div class="grid grid-cols-12 gap-6">
            <div class="col-span-4">
              <mat-label>Item Code</mat-label>
              <mat-form-field appearance="outline">
                <input matInput formControlName="item_code">
              </mat-form-field>
            </div>

            <div class="col-span-8">
              <mat-label>Description **</mat-label>
              <mat-form-field appearance="outline">
                <textarea matInput formControlName="description" rows="2"></textarea>
                <mat-error *ngIf="showItemError(i, 'description')">Description is required</mat-error>
              </mat-form-field>
            </div>

            <div class="col-span-3">
              <mat-label>Quantity **</mat-label>
              <mat-form-field appearance="outline">
                <input matInput type="number" formControlName="quantity" min="0">
                <mat-error *ngIf="showItemError(i, 'quantity')">Quantity is required</mat-error>
              </mat-form-field>
            </div>

            <div class="col-span-3">
              <mat-label>Unit **</mat-label>
              <mat-form-field appearance="outline">
                <mat-select formControlName="unit">
                  <mat-option value="pcs">pcs</mat-option>
                  <mat-option value="kg">kg</mat-option>
                  <mat-option value="ton">ton</mat-option>
                  <mat-option value="m">m</mat-option>
                  <mat-option value="m2">m²</mat-option>
                  <mat-option value="m3">m³</mat-option>
                  <mat-option value="ltr">ltr</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <div class="col-span-3">
              <mat-label>Unit Price **</mat-label>
              <mat-form-field appearance="outline">
                <input matInput type="number" formControlName="unit_price" min="0" step="0.01">
                <span matPrefix>₹&nbsp;</span>
                <mat-error *ngIf="showItemError(i, 'unit_price')">Unit price is required</mat-error>
              </mat-form-field>
            </div>

            <div class="col-span-3">
              <mat-label>Amount</mat-label>
              <div class="amount-display">{{ formatCurrency(calculateItemAmount(i)) }}</div>
            </div>

            <div class="col-span-4">
              <mat-label>HSN Code</mat-label>
              <mat-form-field appearance="outline">
                <input matInput formControlName="hsn_code">
              </mat-form-field>
            </div>

            <div class="col-span-4">
              <mat-label>SAC Code</mat-label>
              <mat-form-field appearance="outline">
                <input matInput formControlName="sac_code">
              </mat-form-field>
            </div>

            <div class="col-span-12">
              <mat-label>Specifications</mat-label>
              <mat-form-field appearance="outline">
                <textarea matInput formControlName="specifications" rows="2"></textarea>
              </mat-form-field>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tax & Charges -->
    <section class="form-section">
      <h2 class="section-title">Tax &amp; Charges</h2>
      <div class="tax-charges-layout">
        <div class="tax-fields grid grid-cols-12 gap-6">
          <div class="col-span-4">
            <mat-label>SGST %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="sgst_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-4">
            <mat-label>CGST %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="cgst_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-4">
            <mat-label>IGST %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="igst_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-4">
            <mat-label>UGST %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="tax2_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-4">
            <mat-label>CESS %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="tax3_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-4">
            <mat-label>TDS %</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="tax1_percentage" min="0" max="100" step="0.01">
              <span matSuffix>%</span>
            </mat-form-field>
          </div>

          <div class="col-span-6">
            <mat-label>Shipping Charges</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="shipping_charges" min="0" step="0.01">
              <span matPrefix>₹&nbsp;</span>
            </mat-form-field>
          </div>

          <div class="col-span-6">
            <mat-label>Other Charges</mat-label>
            <mat-form-field appearance="outline">
              <input matInput type="number" formControlName="other_charges" min="0" step="0.01">
              <span matPrefix>₹&nbsp;</span>
            </mat-form-field>
          </div>
        </div>

        <div class="order-summary">
          <div class="order-summary__title">Order Summary</div>
          <div class="order-summary__row order-summary__row--subtotal">
            <span>Subtotal</span>
            <span>{{ formatCurrency(subtotal) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="cgstAmount > 0">
            <span>CGST &#64;{{ workOrderForm.get('cgst_percentage')?.value }}%</span>
            <span>{{ formatCurrency(cgstAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="sgstAmount > 0">
            <span>SGST &#64;{{ workOrderForm.get('sgst_percentage')?.value }}%</span>
            <span>{{ formatCurrency(sgstAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="igstAmount > 0">
            <span>IGST &#64;{{ workOrderForm.get('igst_percentage')?.value }}%</span>
            <span>{{ formatCurrency(igstAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="ugstAmount > 0">
            <span>UGST &#64;{{ workOrderForm.get('tax2_percentage')?.value }}%</span>
            <span>{{ formatCurrency(ugstAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="cessAmount > 0">
            <span>CESS &#64;{{ workOrderForm.get('tax3_percentage')?.value }}%</span>
            <span>{{ formatCurrency(cessAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="tdsAmount > 0">
            <span>TDS &#64;{{ workOrderForm.get('tax1_percentage')?.value }}%</span>
            <span>{{ formatCurrency(tdsAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="shippingAmount > 0">
            <span>Shipping charges</span>
            <span>{{ formatCurrency(shippingAmount) }}</span>
          </div>
          <div class="order-summary__row" *ngIf="otherChargesAmount > 0">
            <span>Other charges</span>
            <span>{{ formatCurrency(otherChargesAmount) }}</span>
          </div>
          <div class="order-summary__row order-summary__row--total">
            <span>Grand Total</span>
            <span>{{ formatCurrency(grandTotal) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Terms & Conditions -->
    <section class="form-section">
      <h2 class="section-title">Terms &amp; Conditions</h2>
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-6">
          <mat-label>Payment Details</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="payment_details" rows="4"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Payment Terms</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="payment_terms" rows="4"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Notes</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="notes" rows="3"></textarea>
          </mat-form-field>
        </div>

        <div class="col-span-6">
          <mat-label>Special Instructions</mat-label>
          <mat-form-field appearance="outline">
            <textarea matInput formControlName="special_instructions" rows="3"></textarea>
          </mat-form-field>
        </div>
      </div>
    </section>

    <div class="form-actions">
      <button type="button" class="cancel-btn" (click)="goBack()">Cancel</button>
      <app-primary-button>
        <div>{{ editMode ? 'Update' : 'Create' }} Work Order</div>
      </app-primary-button>
    </div>
  </form>
</div>
